<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const { t } = useI18n()
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />
    <main id="main-content" style="width:80%">
      <section
        id="compliance-header"
        class="bg-gradient-to-r from-amber-600 to-amber-800 text-white h-[200px] flex items-center"
      >
        <div class="max-w-7xl mx-auto px-5 w-full">
          <div class="flex items-center space-x-4 mb-4">
            <div>
              <h1 class="text-3xl font-bold">{{ $t('developer.legacy_registration.title') }}</h1>
              <p class="text-amber-100 text-lg">
                {{ $t('developer.legacy_registration.subtitle') }}
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-6 text-sm">
            <span class="bg-white/20 px-3 py-1 rounded-full"
              >{{ $t('developer.legacy_registration.standards.ics_migration') }}</span
            >
            <span class="bg-white/20 px-3 py-1 rounded-full"
              >{{ $t('developer.legacy_registration.standards.cdm_transition') }}</span
            >
            <span class="bg-white/20 px-3 py-1 rounded-full"
              >{{ $t('developer.legacy_registration.standards.vcs_compliance') }}</span
            >
          </div>
        </div>
      </section>

      <section id="compliance-form" class="py-8">
        <div class="max-w-7xl mx-auto px-5">
          <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div class="border-b border-gray-200 p-6">
              <h2 class="text-xl font-semibold text-accent mb-2">
                {{ $t('developer.legacy_registration.registration_form.title') }}
              </h2>
              <p class="text-gray-600">
                {{ $t('developer.legacy_registration.registration_form.subtitle') }}
              </p>
            </div>

            <div class="p-6">
              <form id="compliance-form-main">
                <div id="project-info" class="mb-8">
                  <h3
                    class="text-lg font-semibold text-accent mb-4 flex items-center"
                  >
                    <i class="fas fa-info-circle text-primary mr-2"></i>
                    {{ $t('developer.legacy_registration.registration_form.project_identification.title') }}
                  </h3>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 mb-2"
                        >{{ $t('developer.legacy_registration.registration_form.project_identification.project_name') }}</label
                      >
                      <input
                        type="text"
                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        :placeholder="$t('developer.legacy_registration.registration_form.project_identification.project_name_placeholder')"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 mb-2"
                        >{{ $t('developer.legacy_registration.registration_form.project_identification.project_type') }}</label
                      >
                      <select
                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">{{ $t('developer.legacy_registration.registration_form.project_details.select_category') }}</option>
                        <option value="1">{{ $t('developer.legacy_registration.registration_form.project_details.category_1') }}</option>
                        <option value="2">{{ $t('developer.legacy_registration.registration_form.project_details.category_2') }}</option>
                        <option value="3">{{ $t('developer.legacy_registration.registration_form.project_details.category_3') }}</option>
                      </select>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 mb-2"
                        >{{ $t('developer.legacy_registration.registration_form.project_identification.previous_standard') }}</label
                      >
                      <select
                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">{{ $t('developer.legacy_registration.registration_form.project_details.select_previous_standard') }}</option>
                        <option value="ics">
                          {{ $t('developer.legacy_registration.registration_form.project_details.ics_standard') }}
                        </option>
                        <option value="cdm">
                          {{ $t('developer.legacy_registration.registration_form.project_details.cdm_standard') }}
                        </option>
                        <option value="vcs">
                          {{ $t('developer.legacy_registration.registration_form.project_details.vcs_standard') }}
                        </option>
                        <option value="other">{{ $t('developer.legacy_registration.registration_form.project_details.other_standard') }}</option>
                      </select>
                    </div>
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 mb-2"
                        >{{ $t('developer.legacy_registration.registration_form.project_identification.project_location') }}</label
                      >
                      <input
                        type="text"
                        class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        :placeholder="$t('developer.legacy_registration.registration_form.project_details.location_placeholder')"
                      />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('developer.legacy_registration.registration_form.project_identification.project_description') }}</label
                    >
                    <textarea
                      rows="3"
                      class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      :placeholder="$t('developer.legacy_registration.registration_form.project_details.description_placeholder')"
                    ></textarea>
                  </div>
                </div>

                <div id="document-uploads" class="mb-8">
                  <h3
                    class="text-lg font-semibold text-accent mb-4 flex items-center"
                  >
                    <i class="fas fa-upload text-primary mr-2"></i>
                    {{ $t('developer.legacy_registration.registration_form.document_uploads.title') }}
                  </h3>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div id="core-documents" class="space-y-4">
                      <h4 class="font-medium text-gray-800 mb-3">
                        {{ $t('developer.legacy_registration.registration_form.document_uploads.core_documents') }}
                      </h4>

                      <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-gray-700"
                            >{{ $t('developer.legacy_registration.registration_form.document_uploads.pin.title') }}</span
                          >
                          <span class="text-xs text-error">{{ $t('common.required') }}</span>
                        </div>
                        <div
                          class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                          ></i>
                          <p class="text-sm text-gray-600">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.upload_text') }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_10mb') }}
                          </p>
                        </div>
                      </div>

                      <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-gray-700"
                            >{{ $t('developer.legacy_registration.registration_form.document_uploads.pdd.title') }}</span
                          >
                          <span class="text-xs text-error">{{ $t('common.required') }}</span>
                        </div>
                        <div
                          class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                          ></i>
                          <p class="text-sm text-gray-600">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.upload_text') }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_10mb') }}
                          </p>
                        </div>
                      </div>

                      <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-gray-700"
                            >{{ $t('developer.legacy_registration.registration_form.document_uploads.doe_validation.title') }}</span
                          >
                          <span class="text-xs text-error">{{ $t('common.required') }}</span>
                        </div>
                        <div
                          class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                          ></i>
                          <p class="text-sm text-gray-600">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.upload_text') }}
                          </p>
                          <p class="text-xs text-gray-500">{{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_10mb') }}</p>
                        </div>
                      </div>
                    </div>

                    <div id="compliance-documents" class="space-y-4">
                      <h4 class="font-medium text-gray-800 mb-3">
                        {{ $t('developer.legacy_registration.registration_form.document_uploads.compliance_documents') }}
                      </h4>

                      <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-gray-700"
                            >{{ $t('developer.legacy_registration.registration_form.document_uploads.npramp.title') }}</span
                          >
                          <span class="text-xs text-gray-600">{{ $t('common.required') }}</span>
                        </div>
                        <div
                          class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                          ></i>
                          <p class="text-sm text-gray-600">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.npramp.description') }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_5mb') }}
                          </p>
                        </div>
                      </div>

                      <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-gray-700"
                            >{{ $t('developer.legacy_registration.registration_form.document_uploads.ehmf.title') }}</span
                          >
                          <span class="text-xs text-gray-600">{{ $t('common.required') }}</span>
                        </div>
                        <div
                          class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                          ></i>
                          <p class="text-sm text-gray-600">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.ehmf.description') }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_5mb') }}
                          </p>
                        </div>
                      </div>

                      <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-gray-700"
                            >{{ $t('developer.legacy_registration.registration_form.document_uploads.spcp_fpic.title') }}</span
                          >
                          <span class="text-xs text-gray-600">{{ $t('common.required') }}</span>
                        </div>
                        <div
                          class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                          ></i>
                          <p class="text-sm text-gray-600">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.spcp_fpic.description') }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_5mb') }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-6 border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-gray-700"
                        >{{ $t('developer.legacy_registration.registration_form.document_uploads.crediting_proof.title') }}</span
                      >
                      <span class="text-xs text-error">{{ $t('common.required') }}</span>
                    </div>
                    <div
                      class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                    >
                      <i
                        class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                      ></i>
                      <p class="text-sm text-gray-600">
                        {{ $t('developer.legacy_registration.registration_form.document_uploads.crediting_proof.description') }}
                      </p>
                      <p class="text-xs text-gray-500">{{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_10mb') }}</p>
                    </div>
                  </div>
                </div>

                <div id="financial-info" class="mb-8">
                  <h3
                    class="text-lg font-semibold text-accent mb-4 flex items-center"
                  >
                    <i class="fas fa-dollar-sign text-primary mr-2"></i>
                    {{ $t('developer.legacy_registration.registration_form.financial_info.title') }}
                  </h3>

                  <div
                    class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
                  >
                    <div class="flex items-start space-x-3">
                      <i class="fas fa-info-circle text-amber-600 mt-1"></i>
                      <div>
                        <h4 class="font-medium text-amber-800 mb-1">
                          {{ $t('developer.legacy_registration.registration_form.financial_info.application_fee') }}
                        </h4>
                        <p class="text-sm text-amber-700">
                          {{ $t('developer.legacy_registration.registration_form.financial_info.fee_description') }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-gray-700"
                        >{{ $t('developer.legacy_registration.registration_form.document_uploads.financial_proposal.title') }}</span
                      >
                      <span class="text-xs text-error">{{ $t('common.required') }}</span>
                    </div>
                    <div
                      class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                    >
                      <i
                        class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                      ></i>
                      <p class="text-sm text-gray-600">
                        {{ $t('developer.legacy_registration.registration_form.document_uploads.financial_proposal.description') }}
                      </p>
                      <p class="text-xs text-gray-500">{{ $t('developer.legacy_registration.registration_form.document_uploads.file_formats.pdf_doc_5mb') }}</p>
                    </div>
                  </div>
                </div>

                <div id="declarations" class="mb-8">
                  <h3
                    class="text-lg font-semibold text-accent mb-4 flex items-center"
                  >
                    <i class="fas fa-check-circle text-primary mr-2"></i>
                    {{ $t('developer.legacy_registration.registration_form.declarations.title') }}
                  </h3>

                  <div class="space-y-4">
                    <label class="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        class="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span class="text-sm text-gray-700"
                        >{{ $t('developer.legacy_registration.registration_form.declarations.accurate_documents') }}</span
                      >
                    </label>

                    <label class="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        class="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span class="text-sm text-gray-700"
                        >{{ $t('developer.legacy_registration.registration_form.declarations.compliance_review') }}</span
                      >
                    </label>

                    <label class="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        class="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span class="text-sm text-gray-700"
                        >{{ $t('developer.legacy_registration.registration_form.declarations.fee_agreement') }}</span
                      >
                    </label>

                    <label class="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        class="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span class="text-sm text-gray-700"
                        >{{ $t('developer.legacy_registration.registration_form.declarations.terms_acceptance') }}</span
                      >
                    </label>
                  </div>
                </div>

                <div
                  id="form-actions"
                  class="flex flex-col sm:flex-row gap-4 justify-between pt-6 border-t border-gray-200"
                >
                  <button
                    type="button"
                    class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                  >
                    <i class="fas fa-save mr-2"></i>{{ $t('developer.legacy_registration.registration_form.form_actions.save_draft') }}
                  </button>

                  <div class="flex gap-3">
                    <button
                      type="button"
                      class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                    >
                      {{ $t('developer.legacy_registration.registration_form.form_actions.cancel') }}
                    </button>
                    <button
                      type="submit"
                      class="px-8 py-2 bg-primary text-white rounded-md hover:bg-primary/90 font-medium"
                    >
                      <i class="fas fa-credit-card mr-2"></i>{{ $t('developer.legacy_registration.registration_form.form_actions.submit_pay') }}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
